/**
 * General authentication utilities
 */
import { AxiosError } from "axios";
import { StatusCodes } from "http-status-codes";

import { authServiceEndpoints } from "@/constants/endpoints";

/**
 * Checks if current request is an SSO endpoint
 * Used to prevent logout during login flow
 */
export const isSsoEndpoint = (url?: string): boolean => {
    if (!url) return false;

    return url.includes("/auth/sso/login") || url.includes("/auth/sso/exchange-code");
};

/**
 * Checks if the error should trigger the refresh flow
 *
 * STEP 1 in the refresh flow - Determines if a 401 error should trigger refresh
 *
 * CONDITIONS:
 * - Error status is 401 (Unauthorized)
 * - Request URL is NOT an SSO endpoint (to avoid infinite loops during login)
 *
 * @param error - The axios error that occurred
 * @param requestUrl - The URL of the failed request
 * @returns boolean - true if refresh should be attempted, false otherwise
 */
export const shouldHandleRefresh = (error: AxiosError, requestUrl: string): boolean => {
    return (
        error.response?.status === StatusCodes.UNAUTHORIZED &&
        !isSsoEndpoint(requestUrl) &&
        !requestUrl.includes(authServiceEndpoints.refreshToken) &&
        !shouldSkipAuthRefresh(error)
    );
};

/** Axios lowercases custom headers; read via get() when available. */
export const shouldSkipAuthRefresh = (error: AxiosError): boolean => {
    const headers = error.config?.headers;
    if (!headers) {
        return false;
    }

    const value =
        typeof headers.get === "function"
            ? headers.get("X-Skip-Auth-Refresh")
            : headers["X-Skip-Auth-Refresh"] || headers["x-skip-auth-refresh"];

    return Boolean(value);
};
